import * as THREE from "three";

/**
 * Legacy 2D used a perspective camera at this height with fov 40°.
 * Orthographic frustum half-height is chosen so zoom=1 matches that view scale.
 */
export const TWO_D_CAMERA_Z = 20;
export const TWO_D_CAMERA_FOV_DEG = 40;
export const TWO_D_CAMERA_NEAR = 0.1;
export const TWO_D_CAMERA_FAR = 1000;

/**
 * World-space half-height of the 2D orthographic frustum at zoom=1.
 * Equals perspective half-height at {@link TWO_D_CAMERA_Z} with
 * {@link TWO_D_CAMERA_FOV_DEG}.
 */
export function get2DOrthographicHalfHeight(
  cameraZ: number = TWO_D_CAMERA_Z,
): number {
  return (
    Math.tan(THREE.MathUtils.degToRad(TWO_D_CAMERA_FOV_DEG / 2)) *
    Math.max(cameraZ, 1e-3)
  );
}

/**
 * Apply a world-unit orthographic frustum that preserves the historical
 * perspective zoom scale. Marks the camera manual so R3F does not overwrite
 * left/right/top/bottom from pixel size.
 */
export function apply2DOrthographicFrustum(
  camera: THREE.OrthographicCamera,
  aspect: number,
  cameraZ: number = TWO_D_CAMERA_Z,
): void {
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const halfHeight = get2DOrthographicHalfHeight(cameraZ);
  const halfWidth = halfHeight * safeAspect;
  camera.left = -halfWidth;
  camera.right = halfWidth;
  camera.top = halfHeight;
  camera.bottom = -halfHeight;
  camera.near = TWO_D_CAMERA_NEAR;
  camera.far = TWO_D_CAMERA_FAR;
  camera.updateProjectionMatrix();
}

/**
 * Create the default top-down 2D orthographic camera for the floorplan canvas.
 */
export function createFloorPlan2DOrthographicCamera(
  aspect: number = 16 / 9,
): THREE.OrthographicCamera {
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, TWO_D_CAMERA_NEAR, TWO_D_CAMERA_FAR);
  // R3F skips automatic projection updates when manual is set.
  (camera as THREE.OrthographicCamera & { manual?: boolean }).manual = true;
  camera.position.set(0, 0, TWO_D_CAMERA_Z);
  camera.up.set(0, 1, 0);
  camera.lookAt(0, 0, 0);
  camera.zoom = 1;
  apply2DOrthographicFrustum(camera, aspect, TWO_D_CAMERA_Z);
  return camera;
}
